import { Box, Typography, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useMemo} from "react";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CompositeFilterBase from "./CompositeFilterBase";
import { useBaseEntityInfo } from "../../../../../Hooks/usePagesContent";

/**
 * Validates if composite filter has complete selections
 * Both checkboxes (multiSelectOptions) AND radio buttons (singleSelectOptions) must be selected
 * @param {Object} filter - The composite filter configuration
 * @param {Object} newFilters - Current filter selections
 * @returns {boolean} - True if both checkbox and radio selections are valid
 */
export const isCompositeFilterValid = (filter, newFilters) => {
    // Find checkbox subfilter (multiSelectOptions)
    const checkboxSubFilter = filter.subFilters?.find(
        sf => sf.displayType === 'multiSelectOptions'
    );

    // Find radio button subfilter (singleSelectOptions)
    const radioSubFilter = filter.subFilters?.find(
        sf => sf.displayType === 'singleSelectOptions'
    );

    // Validate checkbox selection: at least one checkbox must be selected
    const hasValidCheckboxSelection = checkboxSubFilter
        ? (newFilters?.[checkboxSubFilter.key]?.length || 0) > 0
        : true; // If no checkbox subfilter exists, consider it valid

    // Validate radio selection: at least one radio button must be selected
    const hasValidRadioSelection = radioSubFilter
        ? (() => {
            const selectedValues = newFilters?.[radioSubFilter.key];

            // No selection at all
            if (!selectedValues || selectedValues.length === 0) {
                return false;
            }

            // Check if the value is valid
            const firstValue = selectedValues[0];
            try {
                // Try to parse as JSON (for rangeValue format)
                const parsed = JSON.parse(firstValue);
                return parsed && typeof parsed === 'object';
            } catch {
                // Not JSON, treat as a regular string/ID value (still valid)
                return Boolean(firstValue);
            }
        })()
        : true; // If no radio subfilter exists, consider it valid

    // Both checkbox AND radio selections must be valid
    return hasValidCheckboxSelection && hasValidRadioSelection;
};

/**
 * The message under the Data Sources checkboxes. Pure so it can be tested
 * without mounting the filter panel.
 * @param {Object} multiSelectSubFilter - The checkbox subfilter config, if any
 * @param {Array} selectedIds - Ticked option ids
 * @param {string} entityPlural - The org's base entity plural label, e.g. "Batteries"
 */
export const getOfflineMessageState = (multiSelectSubFilter, selectedIds, entityPlural = "Batteries") => {
    if (!multiSelectSubFilter) {
        return { type: 'none' };
    }

    // Lower-cased for the middle of a sentence only when it is a plain word
    // ("Batteries"); an acronym such as "EVs" or "BESS Units" is kept as is.
    const label = String(entityPlural || "Batteries");
    const noun = /^[A-Z][a-z]*(\s[A-Z][a-z]*)*$/.test(label) ? label.toLowerCase() : label;
    const options = multiSelectSubFilter.staticData || [];
    const selected = selectedIds || [];

    if (selected.length === 0) {
        return {
            type: 'info',
            message: 'Select at least one data source',
        };
    }
    if (selected.length === options.length) {
        return {
            type: 'error',
            badge: 'Fully Offline',
            message: `Showing ${noun} with no data from any source`,
        };
    }

    const selectedNames = options
        .filter(opt => selected.includes(opt.id))
        .map(opt => opt.value)
        .join(', ');

    return {
        type: 'warning',
        badge: 'Partially Connected',
        message: `Showing ${noun} where ${selectedNames} is not working`,
    };
};

const CompositeSelectFilter = ({ filter, newFilters, onChange, onRadioChange }) => {
    const { t } = useTranslation(['common', 'generic_table', 'filters']);
    const subFilters = filter.subFilters || [];

    // Find the multi-select subfilter (checkboxes - Data Sources)
    const multiSelectSubFilter = useMemo(() => {
        return subFilters.find(sf => sf.displayType === 'multiSelectOptions');
    }, [subFilters]);


    // The org's own entity noun (globalConfig.baseEntity.pluralLabel), the same
    // one the Home page uses: battery orgs filter batteries, not vehicles.
    const { baseEntityTypePluralLable: entityPlural } = useBaseEntityInfo();

    const messageState = useMemo(
        () => getOfflineMessageState(
            multiSelectSubFilter,
            newFilters?.[multiSelectSubFilter?.key],
            entityPlural
        ),
        [multiSelectSubFilter, newFilters, entityPlural]
    );

    const renderMessage = () => {
        if (messageState.type === 'none') return null;

        const getMessageBoxStyles = () => {
            switch (messageState.type) {
                case 'error':
                    return {
                        backgroundColor: '#FEF3F2',
                        borderColor: '#FEE4E2',
                        iconColor: '#D92D20',
                        badgeColor: '#FFFFFF',
                        badgeBgColor: '#D92D20',
                        textColor: '#B42318',
                    };
                case 'warning':
                    return {
                        backgroundColor: '#FFFAEB',
                        borderColor: '#FEDF89',
                        iconColor: '#F79009',
                        badgeColor: '#FFFFFF',
                        badgeBgColor: '#F79009',
                        textColor: '#B54708',
                    };
                case 'info':
                default:
                    return {
                        backgroundColor: '#F3F3F3',
                        borderColor: '#EBEFF3',
                        iconColor: '#9F9F9F',
                        textColor: '#3D4B5A',
                    };
            }
        };

        const styles = getMessageBoxStyles();

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.5,
                    padding: '8px',
                    backgroundColor: styles.backgroundColor,
                    border: `1px solid ${styles.borderColor}`,
                    borderRadius: '6px',
                    mb: 1,
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                }}
            >
                {messageState.type === 'error' ? (
                    <ErrorOutlineIcon sx={{ fontSize: 14, color: styles.iconColor, mt: 0.1 }} />
                ) : messageState.type === 'warning' ? (
                    <WarningAmberIcon sx={{ fontSize: 14, color: styles.iconColor, mt: 0.1 }} />
                ) : null}

                <Box sx={{ flex: 1 }}>
                    {messageState.badge && (
                        <Chip
                            label={messageState.badge}
                            size="small"
                            sx={{
                                height: '16px',
                                fontSize: '10px',
                                fontWeight: 600,
                                color: styles.badgeColor,
                                backgroundColor: styles.badgeBgColor,
                                mb: 0.25,
                                '& .MuiChip-label': {
                                    px: 0.75,
                                    py: 0,
                                },
                            }}
                        />
                    )}
                    <Typography
                        sx={{
                            fontFamily: 'Roboto',
                            fontSize: '11px',
                            lineHeight: '14px',
                            color: styles.textColor,
                            fontWeight: 400,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                        }}
                    >
                        {messageState.message}
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Split subfilters into multi-select (checkboxes) and single-select (radio)
    const checkboxSubFilters = subFilters.filter(sf => sf.displayType === 'multiSelectOptions');
    const radioSubFilters = subFilters.filter(sf => sf.displayType === 'singleSelectOptions');

    return (
        <Box sx={{ height: '180px', overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
            {/* Render checkbox filters first */}
            {checkboxSubFilters.length > 0 && (
                <CompositeFilterBase
                    filter={{ ...filter, subFilters: checkboxSubFilters }}
                    newFilters={newFilters}
                    onChange={onChange}
                    onRadioChange={onRadioChange}
                />
            )}

            {/* Render message between checkbox and radio filters */}
            {renderMessage()}

            {/* Render radio filters after message */}
            {radioSubFilters.length > 0 && (
                <CompositeFilterBase
                    filter={{ ...filter, subFilters: radioSubFilters }}
                    newFilters={newFilters}
                    onChange={onChange}
                    onRadioChange={onRadioChange}
                />
            )}
        </Box>
    );
};

export default CompositeSelectFilter;
